import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { ChevronLeft, Wrench, Trash2, Gauge } from "lucide-react";
import { toast } from "sonner";
import AxleLayout from "../components/AxleLayout";
import VehicleExpenses from "../components/VehicleExpenses";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const REMOVE_REASONS = [
  { v: "aus", l: "Aus (bisa dipakai lagi/di-vulkanisir)" },
  { v: "rusak", l: "Rusak" },
  { v: "ganti", l: "Ganti (kembali ke stok)" },
  { v: "rotasi", l: "Rotasi" },
  { v: "hilang", l: "Hilang/dicuri" },
  { v: "lainnya", l: "Lainnya" },
];

export default function VehicleDetail() {
  const { type, id } = useParams();
  const [data, setData] = useState(null);
  const [availableTyres, setAvailableTyres] = useState([]);
  const [dialog, setDialog] = useState(null); // { pos, tyre }
  const [selectedTyreId, setSelectedTyreId] = useState("");
  const [removeReason, setRemoveReason] = useState("aus");
  const [removeNotes, setRemoveNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [kmDialogOpen, setKmDialogOpen] = useState(false);
  const [newKm, setNewKm] = useState("");

  const load = async () => {
    const r = await api.get(`/vehicles/${type}/${id}`);
    setData(r.data);
    const t = await api.get("/tyres");
    setAvailableTyres(t.data.filter((x) => x.status === "stok"));
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
  }, [type, id]);
  useAutoRefresh(load, 30000);

  const handleInstall = async () => {
    if (!selectedTyreId) return toast.error("Pilih ban dari stok");
    setBusy(true);
    try {
      await api.post("/installations", {
        tyre_id: selectedTyreId,
        vehicle_type: type,
        vehicle_id: id,
        position: dialog.pos,
      });
      toast.success(`Ban terpasang di ${dialog.pos}`);
      setDialog(null);
      setSelectedTyreId("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await api.post(`/installations/remove/${dialog.tyre.id}`, {
        reason: removeReason,
        notes: removeNotes,
      });
      toast.success("Ban dilepas");
      setDialog(null);
      setRemoveNotes("");
      setRemoveReason("aus");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateKm = async () => {
    setBusy(true);
    try {
      await api.patch(`/trucks/${id}/km`, { current_km: Number(newKm) });
      toast.success("KM diperbarui");
      setKmDialogOpen(false);
      setNewKm("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="text-slate-500">Memuat...</div>;

  const vehicle = data.vehicle;
  const identifier = type === "truck" ? vehicle.plat_nomor : vehicle.code;
  const backLink = type === "truck" ? "/trucks" : "/trailers";

  return (
    <div>
      <Link to={backLink} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-link">
        <ChevronLeft className="w-4 h-4" /> Kembali
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="border-2 border-slate-900 bg-white p-6 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {type === "truck" ? "Head Truck" : "Trailer"}
            </div>
            <div className="font-mono font-black text-4xl text-slate-900 mt-2" data-testid="vehicle-identifier">
              {identifier}
            </div>
            {type === "truck" ? (
              <div className="mt-2 text-slate-600">{vehicle.brand} {vehicle.model}</div>
            ) : (
              <div className="mt-2 text-slate-600 font-mono">{vehicle.size}</div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Jumlah Ban</div>
              <div className="font-mono font-bold">{vehicle.num_tyres}</div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Terpasang</div>
              <div className="font-mono font-bold">{Object.values(data.positions).filter(Boolean).length}</div>
            </div>
            {type === "truck" && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Odometer</div>
                  <div className="font-mono font-bold text-lg" data-testid="truck-km">{(vehicle.current_km || 0).toLocaleString()} km</div>
                </div>
                <Button
                  onClick={() => { setNewKm(String(vehicle.current_km || 0)); setKmDialogOpen(true); }}
                  data-testid="update-km-btn"
                  className="w-full mt-3 h-11 bg-slate-900 hover:bg-slate-800 text-orange-400 font-bold uppercase tracking-wider rounded-sm"
                >
                  <Gauge className="w-4 h-4 mr-2" /> Update KM
                </Button>
              </div>
            )}
          </div>

          <div className="border border-slate-200 bg-white p-4 rounded-sm">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Cara Pakai</div>
            <div className="text-sm text-slate-600 space-y-1">
              <div>• Klik posisi kosong → pilih ban dari stok</div>
              <div>• Klik ban terpasang → lepas / catat kondisi</div>
              <div>• Semua aksi tersimpan otomatis</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 flex items-center justify-center overflow-x-auto">
          <AxleLayout
            numTyres={vehicle.num_tyres}
            vehicleType={type}
            positions={data.positions}
            onPositionClick={(pos, tyre) => {
              setDialog({ pos, tyre });
              setSelectedTyreId("");
            }}
          />
        </div>
      </div>

      {/* Riwayat Biaya section */}
      <VehicleExpenses vehicleType={type} vehicleId={id} currentKm={vehicle.current_km} />

      {/* Install / Remove Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">
              Posisi <span className="font-mono text-orange-600">{dialog?.pos}</span>
            </DialogTitle>
          </DialogHeader>

          {dialog?.tyre ? (
            // Remove flow
            <div className="space-y-4">
              <div className="border-2 border-slate-900 p-4 rounded-sm bg-slate-50">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Ban terpasang</div>
                <div className="font-mono text-2xl font-black text-slate-900 mt-1">{dialog.tyre.serial_short}</div>
                <div className="text-sm text-slate-600 mt-1">
                  {dialog.tyre.brand} · {dialog.tyre.size}
                </div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Alasan Lepas *</Label>
                <Select value={removeReason} onValueChange={setRemoveReason}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="remove-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REMOVE_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Catatan (opsional)</Label>
                <Input
                  data-testid="remove-notes"
                  value={removeNotes}
                  onChange={(e) => setRemoveNotes(e.target.value)}
                  className="h-12 rounded-sm"
                  placeholder="Contoh: kembang habis di sisi luar"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Batal</Button>
                <Button
                  onClick={handleRemove}
                  disabled={busy}
                  data-testid="remove-submit"
                  className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase rounded-sm btn-industrial"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> {busy ? "..." : "Lepas Ban"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Install flow
            <div className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Pilih Ban dari Stok *</Label>
                <Select value={selectedTyreId} onValueChange={setSelectedTyreId}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="install-tyre-select">
                    <SelectValue placeholder="-- Pilih ban --" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTyres.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">Tidak ada ban di stok. Tambahkan ban dulu di menu Ban.</div>
                    ) : (
                      availableTyres.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="font-mono font-bold">{t.serial_short}</span> · {t.brand} · {t.size}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Batal</Button>
                <Button
                  onClick={handleInstall}
                  disabled={busy || !selectedTyreId}
                  data-testid="install-submit"
                  className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial"
                >
                  <Wrench className="w-4 h-4 mr-1" /> {busy ? "..." : "Pasang Ban"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update KM dialog */}
      <Dialog open={kmDialogOpen} onOpenChange={setKmDialogOpen}>
        <DialogContent className="rounded-sm border-2 border-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Update KM Odometer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="uppercase text-xs tracking-widest font-bold">KM Saat Ini *</Label>
              <Input
                data-testid="new-km-input"
                type="number"
                min={0}
                value={newKm}
                onChange={(e) => setNewKm(e.target.value)}
                className="h-12 rounded-sm font-mono text-lg"
              />
              <p className="text-xs text-slate-500 mt-1">
                KM baru tidak boleh lebih kecil dari KM sebelumnya. Digunakan untuk menghitung umur pakai ban dan alert rotasi.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setKmDialogOpen(false)} className="rounded-sm">Batal</Button>
              <Button onClick={handleUpdateKm} disabled={busy || !newKm} data-testid="km-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                <Gauge className="w-4 h-4 mr-1" /> {busy ? "..." : "Simpan"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
